
import React, { createContext, useContext, useState, ReactNode } from 'react';

interface User {
  name: string;
  email: string;
  role: 'enterprise' | 'supplier';
  companyName: string; // Used to filter data for suppliers
}

interface AuthContextType {
  isAuthenticated: boolean;
  login: (email: string, role?: 'enterprise' | 'supplier') => void;
  logout: () => void;
  user: User | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('octosuite_auth') === 'true';
  });
  
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('octosuite_user');
    return saved ? JSON.parse(saved) : null;
  });

  const login = (email: string, role: 'enterprise' | 'supplier' = 'enterprise') => {
    setIsAuthenticated(true);
    
    let userData: User;

    if (role === 'supplier') {
      // Simulate a Supplier User (Hardcoded mapping for demo purposes)
      userData = { 
        name: 'Supplier Representative', 
        email, 
        role: 'supplier',
        companyName: 'Supplier Portal' // This matches a supplier in inventory.ts
      };
    } else {
      // Standard Enterprise User
      userData = { 
        name: 'Daniel Kohler', 
        email, 
        role: 'enterprise',
        companyName: 'VMI Hub Enterprise'
      };
    }

    setUser(userData);
    localStorage.setItem('octosuite_auth', 'true');
    localStorage.setItem('octosuite_user', JSON.stringify(userData));
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('octosuite_auth');
    localStorage.removeItem('octosuite_user');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
